/**
 * <carton-builder>
 *
 * Builds a mixed carton from real pack products and adds them to the cart in one call,
 * led by a $0.00 "Mixed Carton" anchor line. The anchor is what the Postcode Shipping app
 * counts to charge shipping once per carton — the packs carry the money.
 *
 * All data (configs, anchor variant IDs, packs, money format) comes from the JSON blob the
 * section renders. Nothing about the store is hardcoded here.
 */
class CartonBuilder extends HTMLElement {
  constructor() {
    super();

    this.data = this.parseData();
    if (!this.data) return;

    this.packsByVariantId = new Map(this.data.packs.map((pack) => [String(pack.variantId), pack]));

    /** @type {string|null} */
    this.selectedConfigKey = null;
    /** @type {Map<string, number>} variantId -> quantity */
    this.quantities = new Map();

    this.els = {
      configInputs: Array.from(this.querySelectorAll('[data-config-input]')),
      packsStep: this.querySelector('[data-packs-step]'),
      groups: Array.from(this.querySelectorAll('[data-group]')),
      summary: this.querySelector('[data-summary]'),
      summaryConfig: this.querySelector('[data-summary-config]'),
      summaryPacks: this.querySelector('[data-summary-packs]'),
      summaryTotal: this.querySelector('[data-summary-total]'),
      summarySavings: this.querySelector('[data-summary-savings]'),
      clear: this.querySelector('[data-clear]'),
      add: this.querySelector('[data-add]'),
      addText: this.querySelector('[data-add-text]'),
      error: this.querySelector('[data-error]'),
    };
  }

  connectedCallback() {
    if (!this.data) return;

    this.els.configInputs.forEach((input) => {
      input.addEventListener('change', () => this.selectConfig(input.value));
    });

    this.addEventListener('click', (event) => {
      const stepButton = event.target.closest('[data-step]');
      if (stepButton) {
        const packEl = stepButton.closest('[data-pack]');
        if (packEl) this.step(packEl.dataset.pack, Number(stepButton.dataset.step));
      }
    });

    this.els.clear?.addEventListener('click', () => this.clearSelection());
    this.els.add?.addEventListener('click', () => this.addToCart());

    this.renderConfigFromPrices();

    // Restore the checked radio if the browser preserved it across a soft reload.
    const checked = this.els.configInputs.find((input) => input.checked);
    if (checked) this.selectConfig(checked.value);
  }

  parseData() {
    const script = this.querySelector('[data-carton-builder-config]');
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data.packs) || !Array.isArray(data.configs)) return null;
      return data;
    } catch (error) {
      console.error('[carton-builder] Could not parse section data.', error);
      return null;
    }
  }

  /* ------------------------------------------------------------------ state */

  get config() {
    return this.data.configs.find((config) => config.key === this.selectedConfigKey) || null;
  }

  /** Required count for a pack type under the current config, or 0 if unused. */
  requiredFor(packType) {
    const group = this.config?.groups.find((g) => g.packType === packType);
    return group ? group.count : 0;
  }

  /** How many packs of a type are currently selected. */
  selectedIn(packType) {
    let total = 0;
    this.quantities.forEach((quantity, variantId) => {
      if (this.packsByVariantId.get(variantId)?.packType === packType) total += quantity;
    });
    return total;
  }

  get isComplete() {
    if (!this.config) return false;
    return this.config.groups.every((group) => this.selectedIn(group.packType) === group.count);
  }

  get totalCents() {
    let total = 0;
    this.quantities.forEach((quantity, variantId) => {
      const pack = this.packsByVariantId.get(variantId);
      if (pack) total += pack.price * quantity;
    });
    return total;
  }

  /**
   * What the selected packs would cost at their compare-at prices. Packs without a compare-at
   * (or with one at or below the real price, which Shopify treats as no sale) count at their
   * own price, so they neither inflate nor deflate the saving.
   */
  get compareAtTotalCents() {
    let total = 0;
    this.quantities.forEach((quantity, variantId) => {
      const pack = this.packsByVariantId.get(variantId);
      if (!pack) return;
      const each = pack.compareAtPrice > pack.price ? pack.compareAtPrice : pack.price;
      total += each * quantity;
    });
    return total;
  }

  get savingsCents() {
    return this.compareAtTotalCents - this.totalCents;
  }

  selectConfig(key) {
    if (key === this.selectedConfigKey) return;
    this.selectedConfigKey = key;
    this.quantities.clear(); // Changing config resets Step 2.
    this.clearError();
    this.render();
  }

  step(variantId, delta) {
    const pack = this.packsByVariantId.get(variantId);
    if (!pack || !this.config) return;

    const required = this.requiredFor(pack.packType);
    const current = this.quantities.get(variantId) || 0;
    const roomLeft = required - this.selectedIn(pack.packType);

    let next = current + delta;
    if (next < 0) next = 0;
    if (delta > 0 && roomLeft <= 0) return;
    if (next > current + roomLeft) next = current + roomLeft;

    if (next === 0) {
      this.quantities.delete(variantId);
    } else {
      this.quantities.set(variantId, next);
    }

    this.clearError();
    this.render();
  }

  clearSelection() {
    this.quantities.clear();
    this.clearError();
    this.render();
  }

  /* ---------------------------------------------------------------- render */

  render() {
    const config = this.config;

    this.els.packsStep.hidden = !config;
    this.els.summary.hidden = !config;
    if (!config) return;

    this.els.groups.forEach((groupEl) => {
      const packType = groupEl.dataset.group;
      const required = this.requiredFor(packType);
      groupEl.hidden = required === 0;
      if (required === 0) return;

      const selected = this.selectedIn(packType);
      const noun = packType === 'six' ? '6-Packs' : '4-Packs';

      groupEl.querySelector('[data-group-title]').textContent = `Choose ${required} × ${noun}`;
      groupEl.querySelector('[data-group-counter]').textContent = `${selected} of ${required} selected`;
      groupEl.classList.toggle('carton-builder__group--full', selected === required);

      groupEl.querySelectorAll('[data-pack]').forEach((packEl) => {
        const quantity = this.quantities.get(packEl.dataset.pack) || 0;
        packEl.querySelector('[data-pack-count]').textContent = String(quantity);
        packEl.classList.toggle('carton-builder__pack--selected', quantity > 0);
        packEl.querySelector('[data-step="-1"]').disabled = quantity === 0;
        packEl.querySelector('[data-step="1"]').disabled = selected >= required;
      });
    });

    this.els.summaryConfig.textContent = `${config.label} — ${config.summary}`;
    this.els.summaryPacks.textContent = this.selectionSummary() || 'No packs selected yet.';
    this.els.summaryTotal.textContent = this.formatMoney(this.totalCents);

    const savings = this.savingsCents;
    if (this.els.summarySavings) {
      this.els.summarySavings.textContent = savings > 0 ? `You save ${this.formatMoney(savings)}` : '';
      this.els.summarySavings.hidden = savings <= 0;
    }

    this.els.add.disabled = !this.isComplete;
  }

  /** "3× Alcoholic Ginger Beer 4-Pack, 2× Amber Lager 6-Pack" — 6-Packs first, matching group order. */
  selectionSummary() {
    return this.data.packs
      .filter((pack) => this.quantities.has(String(pack.variantId)))
      .map((pack) => `${this.quantities.get(String(pack.variantId))}× ${pack.title}`)
      .join(', ');
  }

  /** "from $X" per config card, using the cheapest pack of each required type. */
  renderConfigFromPrices() {
    this.data.configs.forEach((config) => {
      const target = this.querySelector(`[data-config-from="${config.key}"]`);
      if (!target) return;

      let from = 0;
      const priceable = config.groups.every((group) => {
        const prices = this.data.packs
          .filter((pack) => pack.packType === group.packType)
          .map((pack) => pack.price);
        if (prices.length === 0) return false;
        from += Math.min(...prices) * group.count;
        return true;
      });

      target.textContent = priceable ? `from ${this.formatMoney(from)}` : '';
    });
  }

  /* ------------------------------------------------------------ add to cart */

  async addToCart() {
    const config = this.config;
    if (!config || !this.isComplete || this.loading) return;

    if (!config.anchorVariantId) {
      this.showError(
        'This carton is not fully set up yet. Please contact us and we’ll take your order directly.'
      );
      console.error('[carton-builder] Missing anchor variant ID for config', config.key);
      return;
    }

    const properties = { Carton: config.cartonLabel };
    const items = [
      // Anchor first: $0.00, one per carton. This is the line the shipping app counts.
      // `_carton_anchor` is underscore-prefixed, so Liquid and the admin order can see it but
      // the customer-facing cart and checkout never render it. The cart snippets use it to
      // identify the anchor row instead of inferring it from a $0 price.
      { id: config.anchorVariantId, quantity: 1, properties: { ...properties, _carton_anchor: '1' } },
      ...this.data.packs
        .filter((pack) => this.quantities.has(String(pack.variantId)))
        .map((pack) => ({
          id: pack.variantId,
          quantity: this.quantities.get(String(pack.variantId)),
          properties,
        })),
    ];

    this.setLoading(true);
    this.clearError();

    const cartTarget = this.cartTarget();
    const sections = cartTarget?.getSectionsToRender?.().map((section) => section.id);
    cartTarget?.setActiveElement?.(document.activeElement);

    try {
      const body = { items };
      if (sections?.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        this.showError(result.description || result.message || 'Sorry, that carton could not be added.');
        return;
      }

      this.refreshCart(result, cartTarget);
      this.clearSelection();
    } catch (error) {
      console.error('[carton-builder] Add to cart failed.', error);
      this.showError('Sorry, something went wrong adding your carton. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  /** Whichever cart UI the theme is configured for. Same order as Refresh's product-form.js. */
  cartTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  refreshCart(result, cartTarget) {
    // `publish` and `PUB_SUB_EVENTS` are theme globals. PUB_SUB_EVENTS is declared with `const`,
    // so it is NOT a property of window — it has to be read bare, behind a typeof guard.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'carton-builder', cartData: result });
    }

    if (cartTarget?.renderContents) {
      cartTarget.classList?.remove('is-empty');
      cartTarget.renderContents(result);
      return;
    }

    // No drawer or notification in this theme layout (e.g. the cart page itself).
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /* ------------------------------------------------------------------- ui */

  setLoading(loading) {
    this.loading = loading;
    this.els.add.classList.toggle('carton-builder__add--loading', loading);
    this.els.add.disabled = loading || !this.isComplete;
    this.els.addText.textContent = loading ? 'Adding…' : 'Add to cart';
  }

  showError(message) {
    this.els.error.textContent = message;
    this.els.error.hidden = false;
  }

  clearError() {
    this.els.error.textContent = '';
    this.els.error.hidden = true;
  }

  /* --------------------------------------------------------------- money */

  formatMoney(cents) {
    const format = this.data.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholder, value);
  }
}

function formatWithDelimiters(cents, precision, thousands = ',', decimal = '.') {
  if (!Number.isFinite(cents)) return '0';

  const fixed = (cents / 100).toFixed(precision);
  const [whole, fraction] = fixed.split('.');
  const withThousands = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

  return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
}

if (!customElements.get('carton-builder')) {
  customElements.define('carton-builder', CartonBuilder);
}
